import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { MatterDialog } from "@/components/forms";
import { Plus, ExternalLink, Landmark, User, Swords } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  Active: "bg-green-50 text-green-700 border-green-200",
  Pending: "bg-amber-50 text-amber-800 border-amber-200",
  "On hold": "bg-sky-50 text-sky-800 border-sky-200",
  Closed: "bg-slate-100 text-slate-500 border-slate-200",
  Archived: "bg-slate-200 text-slate-500 border-slate-300",
};

const FILTERS = ["Active", "Pending", "On hold", "Closed", "Archived", "All"];

export default function Matters() {
  const { t } = useT();
  const [matters, setMatters] = useState(null);
  const [filter, setFilter] = useState("Active");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => {
    const params = filter === "All" ? {} : { status: filter };
    api.get("/matters", { params }).then((r) => setMatters(r.data)).catch(() => {});
  };

  useEffect(load, [filter]);

  return (
    <div data-testid="matters-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-slate-900">{t("matters.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("matters.subtitle")}</p>
        </div>
        <Button
          data-testid="create-matter-button"
          onClick={() => setDialogOpen(true)}
          className="bg-slate-900 hover:bg-slate-800"
        >
          <Plus className="h-4 w-4 mr-1.5" /> {t("matters.new")}
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="matter-status-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`filter-${f.toLowerCase().replace(" ", "-")}`}
            onClick={() => setFilter(f)}
            className={`px-3.5 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              filter === f
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
          >
            {f === "All" ? t("invoices.filter_all") : t(`val.${f}`)}
          </button>
        ))}
      </div>

      {!matters ? (
        <div className="text-sm text-slate-500" data-testid="matters-loading">{t("matters.loading")}</div>
      ) : matters.length === 0 ? (
        <div className="text-sm text-slate-500" data-testid="matters-empty">{t("matters.empty")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="matters-grid">
          {matters.map((m) => (
            <div
              key={m.id}
              data-testid={`matter-card-${m.internal_ref || m.id}`}
              className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150"
            >
              <div className="flex items-start justify-between gap-3">
                <Link to={`/matters/${m.id}`} className="min-w-0" data-testid={`matter-link-${m.internal_ref || m.id}`}>
                  <h3 className="font-serif text-lg text-slate-900 leading-snug hover:underline">{m.name}</h3>
                </Link>
                <span
                  data-testid={`matter-status-badge-${m.internal_ref || m.id}`}
                  className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGE[m.status] || ""}`}
                >
                  {t(`val.${m.status}`)}
                </span>
              </div>

              <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="truncate">{m.client}</span>
                </div>
                {m.opposing_party && (
                  <div className="flex items-center gap-2">
                    <Swords className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{t("matters.vs")} {m.opposing_party}</span>
                  </div>
                )}
                {(m.court_authority || m.court_case_number) && (
                  <div className="flex items-center gap-2">
                    <Landmark className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">
                      {m.court_authority}
                      {m.court_case_number ? ` · ${m.court_case_number}` : ""}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>{t("matters.ref", { ref: m.internal_ref || "—" })}</span>
                <span>
                  {t("matters.counts", { d: m.open_deadlines, t: m.open_tasks })}
                </span>
                {m.dropbox_link && (
                  <a
                    href={m.dropbox_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`matter-dropbox-${m.internal_ref || m.id}`}
                    className="flex items-center gap-1 text-slate-600 hover:text-slate-900 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" /> Dropbox
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <MatterDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={() => {
          setDialogOpen(false);
          load();
          toast.success(t("matter.created"));
        }}
      />
    </div>
  );
}
